import type { VideoComposition } from "@hightouch/ad-studio-renderer";

/**
 * Player-level config that the parent forwards to the iframe's `<Player>`.
 * Maps 1:1 to the props of the same name on the inline `CompositionPlayer`.
 */
export interface PlayerOptions {
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  initiallyMuted?: boolean;
  clickToPlay?: boolean;
  spaceKeyToPlayOrPause?: boolean;
}

/**
 * Messages sent from the parent (editor) to the iframe (preview player).
 *
 * All `ad:` references in `setComposition` are looked up at render time
 * using `componentAssets` + `resolvedRefs` (an in-iframe asset resolver).
 * The parent has no `/agents/files` access from the iframe — every URL
 * must be pre-resolved before sending.
 */
export type InboundMessage =
  | {
      type: "setComposition";
      composition: VideoComposition;
      /** Filename → presigned URL. Default fallback for `ad:<filename>` refs. */
      componentAssets: Record<string, string>;
      /**
       * Full `ad:` ref → resolved value map for inline overrides (e.g.
       * live-edited SVG content). Takes precedence over `componentAssets`.
       */
      resolvedRefs: Record<string, string>;
    }
  | {
      /**
       * Self-contained CSS `@font-face` rules from the parent's
       * `FontInjectionProvider`. File fonts are pre-inlined as base64 data
       * URLs by the parent (the iframe can't auth to fetch them itself).
       * Shipped as a separate message because rules are heavy (potentially
       * MB) and change rarely — sending them inside every `setComposition`
       * meant re-serializing the full font payload on every drag frame.
       */
      type: "setFontRules";
      fontRules: string[];
    }
  | {
      /**
       * Player-level options (`controls`, `autoPlay`, etc.) that the parent
       * forwards through. Separate from `setComposition` because they're
       * stable and shouldn't re-ship on every drag tick.
       */
      type: "setPlayerOptions";
      options: PlayerOptions;
    }
  | { type: "play" }
  | { type: "pause" }
  | { type: "toggle" }
  | { type: "seekTo"; frame: number }
  | { type: "mute" }
  | { type: "unmute" };

/**
 * Messages sent from the iframe back to the parent.
 *
 * `frameChanged` fires on every Remotion frame during playback. The parent
 * should treat it as a cheap signal — write to a ref, only setState when
 * something the UI actually depends on changes (alive-layer membership,
 * second-tick boundary, etc.). Naively driving React renders off
 * `frameChanged` will cause jank.
 */
export type OutboundMessage =
  | { type: "ready" }
  | { type: "error"; kind: string; message: string; stack?: string }
  | { type: "frameChanged"; frame: number }
  | { type: "playStateChanged"; isPlaying: boolean }
  | { type: "muteStateChanged"; isMuted: boolean }
  | { type: "sizeChanged"; width: number; height: number }
  // Navigation key pressed while the iframe held focus. Keyboard events don't
  // cross the sandbox boundary, so the parent re-dispatches these for its
  // page-level hotkeys (e.g. left/right to cycle ad size variants).
  | { type: "keydown"; key: string };

/**
 * Marker tag included on every message so we can ignore unrelated postMessage
 * traffic (extensions, devtools, etc.) without false positives.
 */
export const MESSAGE_TAG = "ad-preview-frame";

export interface TaggedMessage<T> {
  __tag: typeof MESSAGE_TAG;
  payload: T;
}

export function tagMessage<T>(payload: T): TaggedMessage<T> {
  return { __tag: MESSAGE_TAG, payload };
}

export function isTaggedMessage<T>(value: unknown): value is TaggedMessage<T> {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { __tag?: unknown }).__tag === MESSAGE_TAG
  );
}
